/**
 * Use the Editorial Feature to highlight marketing props.or an event (i.e. Skin Equity landing page or Love Your Skin event).
 *
 * @module views/components/EditorialFeature
 * @memberof -Common
 */
import './EditorialFeature.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';


/**
 * Represents a EditorialFeature component
 *
 * @method
 * @param {EditorialFeatureProps} props - React properties passed from composition
 * @returns EditorialFeature
 */
export const EditorialFeature = function( props ){
  const { desktopImage, tabletImage, mobileImage, title, description, mirror, linkAction } = props;
  const { breakpoint } = useDeviceInflection();

  const isDesktop = ( breakpoint.CURRENT_BREAKPOINT === 'XL' || breakpoint.CURRENT_BREAKPOINT === 'LG' ) && desktopImage;
  const isTablet = breakpoint.CURRENT_BREAKPOINT === 'MD' && tabletImage;
  const isMobile = breakpoint.CURRENT_BREAKPOINT === 'SM' && mobileImage;

  return (
    <div className='EditorialFeature'>
      <div className='EditorialFeature__content'>
        <div
          className={ classNames( 'EditorialFeature__Image', {
            'EditorialFeature__Image--mirrored': mirror
          } ) }
        >
          { isDesktop && <UltaAsset { ...spreadProps( { ...desktopImage } ) } /> }
          { isTablet && <UltaAsset { ...spreadProps( { ...tabletImage } ) } /> }
          { isMobile && <UltaAsset { ...spreadProps( { ...mobileImage } ) } /> }
        </div>
        {
          <div
            className={ classNames( 'EditorialFeature__Details', {
              'EditorialFeature__Details--mirrored': mirror
            } ) }
          >
            { title?.htmlTag && (
              <div className='EditorialFeature__Title'>
                <Text
                  htmlTag={ title.htmlTag }
                  textStyle={ title.textStyle }
                  color={ title.color }
                  textAlign={ title.textAlign }
                >
                  { title.text }
                </Text>
              </div>
            ) }
            <div className='EditorialFeature__Subtitle'>
              <Text htmlTag='p'
                textStyle='subtitle-2'
              >
                { description }
              </Text>
            </div>
            { linkAction?.label && linkAction.url && (
              <div className='EditorialFeature__Link'>
                <Link_Huge regular
                  action={ linkAction }
                  withHover
                >
                  { linkAction.label }
                </Link_Huge>
              </div>
            ) }
          </div>
        }
      </div>
    </div>
  );
};


/**
 * Property type definitions
 * @typedef EditorialFeatureProps
 * @type {object}
 * @property {object} mobileImage - image for mobile inflection
 * @property {object} tabletImage - image for tablet inflection
 * @property {object} desktopImage - image for desktop inflection
 * @property {object} title - value for the Title
 * @property {string} description - value the Description
 * @property {boolean} mirror - value for the mirror property
 * @property {object} linkAction - represents the link
 */
export const propTypes =  {
  mobileImage: PropTypes.object.isRequired,
  tabletImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  title: PropTypes.object,
  description: PropTypes.string,
  mirror: PropTypes.bool,
  linkAction:PropTypes.object
};


EditorialFeature.propTypes = propTypes;

export default EditorialFeature;
